import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import ActiveLink, { NavCounter } from '../components/ActiveLink';
import {
  ACTIVE_CLASS,
  ENABLE_INVESTING_ACCOUNT_CONTROLLER,
  ENABLE_UNIFIED_UPLOAD,
  ENABLE_REFERRAL_CONTROLLER,
  ENABLE_SYNC_UNIT_CONTROLLER,
  ENABLE_TAX_DECLARATION_CONTROLLER,
  ENABLE_TAX_PLANNER_CONTROLLER,
} from '../constants/constants';
import useToken from '../hooks/use-token';
import {
  useReadSynchronizingInvestingAccountsQuery,
  useReadTaxPlannerAvailabilityQuery,
  useSynchronizedFilesQuery,
} from '../store';
import { MY_ACCOUNTS_PATH, SYNC_UNITS_PATH } from '../constants/syncRoutes';
import useNavigation from '../hooks/use-navigation';
import { UNIFIED_UPLOAD_PATH } from '../pages/UnifiedUploadPage';
import Link from './Link';

// Icons of the mock (sync-mock.html aside): inline SVG 17 px, stroke 1.6, currentColor.
const NAV_ICONS = {
  home: <path d="M3 11l9-8 9 8M5 10v10h14V10M10 20v-6h4v6" />,
  upload: <path d="M12 3v12M7 8l5-5 5 5M4 21h16" />,
  sync: <path d="M20 12a8 8 0 0 1-14 5.3M4 12a8 8 0 0 1 14-5.3M18 3v4h-4M6 21v-4h4" />,
  accounts: <path d="M4 6h16M4 12h16M4 18h10" />,
  calc: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h4" />
    </>
  ),
  referral: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M18 14a6.5 6.5 0 0 1 3.5 6" />
    </>
  ),
  planner: <path d="M3 20h18M5 16l4-5 4 3 6-8M15 6h4v4" />,
  cabinet: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
};

const isUnder = (path, base) => path === base || path?.startsWith(`${base}/`);

// The sidebar item that owns the current screen: SCR-01 «Завантаження», SCR-02/03 «Синхронізація», SCR-04/05 «Мої рахунки».
function isNavItemActive(currentPath, to) {
  switch (to) {
    case '/':
      return currentPath === '/';
    case UNIFIED_UPLOAD_PATH:
      return isUnder(currentPath, UNIFIED_UPLOAD_PATH);
    case SYNC_UNITS_PATH:
      return isUnder(currentPath, SYNC_UNITS_PATH);
    case MY_ACCOUNTS_PATH:
      return currentPath === MY_ACCOUNTS_PATH || currentPath?.startsWith('/accounts/');
    case '/taxreports':
      return currentPath === '/taxreports' || currentPath === '/declaration';
    case '/referral':
      return currentPath === '/referral' || currentPath === '/referral-info';
    default:
      return currentPath === to;
  }
}

// Sidebar / top-bar item of the broker-report-sync shell: no pseudo-element corners, the counter is the last flex child.
export function NavItem({ to, icon, active, onClick, children }) {
  const { navigate, currentPath } = useNavigation();
  const isActive = active ?? isNavItemActive(currentPath, to);
  const handleClick =
    onClick ||
    ((event) => {
      if (event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      navigate(to);
    });
  return (
    <a
      href={to}
      onClick={handleClick}
      aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'flex items-center gap-2 px-3 py-2.5 text-sm rounded-[10px] whitespace-nowrap',
        isActive ? 'bg-surface-alt text-text' : 'text-text-muted',
      )}>
      {icon && (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" className="shrink-0">
          {NAV_ICONS[icon]}
        </svg>
      )}
      {children}
    </a>
  );
}

function Sidebar() {
  const { t } = useTranslation();
  const token = useToken();
  let params = {
    dataProvider: '',
    page: 0,
    size: 1,
  };
  const { data } = useSynchronizedFilesQuery(params);
  const syncUnitNavigation = ENABLE_INVESTING_ACCOUNT_CONTROLLER && ENABLE_SYNC_UNIT_CONTROLLER;
  // «Синхронізація» counter — the length of the last synchronizing list; SCR-02 polling refreshes the same entry.
  const { data: synchronizing } = useReadSynchronizingInvestingAccountsQuery(undefined, { skip: !syncUnitNavigation || !token });
  const openSyncUnits = synchronizing?.synchronizingInvestingAccounts?.length || 0;

  // Fail closed: the planner item shows only once availability has answered enabled === true.
  const { data: plannerAvailability } = useReadTaxPlannerAvailabilityQuery(undefined, { skip: !ENABLE_TAX_PLANNER_CONTROLLER || !token });
  const plannerAvailable = ENABLE_TAX_PLANNER_CONTROLLER && plannerAvailability?.enabled === true;

  let accountLink =
    ENABLE_UNIFIED_UPLOAD && ENABLE_INVESTING_ACCOUNT_CONTROLLER
      ? UNIFIED_UPLOAD_PATH
      : '/accounts';
  if (data?.content?.length > 0) {
    accountLink = '/reports';
  }

  const links = [{ label: t('sidebar.links.home'), path: '/', icon: 'home', shellIcon: 'home' }];
  if (syncUnitNavigation) {
    // broker-report-sync AppShell: «Завантаження · Синхронізація · Мої рахунки».
    links.push(
      { label: t('sidebar.links.upload'), path: UNIFIED_UPLOAD_PATH, icon: 'upload' },
      { label: t('sidebar.links.sync'), path: SYNC_UNITS_PATH, icon: 'sync', count: openSyncUnits },
      { label: t('sidebar.links.myAccounts'), path: MY_ACCOUNTS_PATH, icon: 'accounts' },
    );
  } else if (ENABLE_INVESTING_ACCOUNT_CONTROLLER) {
    links.push({
      label: t('sidebar.links.accounts'),
      path: accountLink,
      icon: 'list',
      shellIcon: 'accounts',
    });
  }
  ENABLE_TAX_DECLARATION_CONTROLLER &&
    links.push({
      label: t('sidebar.links.taxReports'),
      path: '/taxreports',
      icon: 'paper',
      shellIcon: 'calc',
    });
  ENABLE_REFERRAL_CONTROLLER &&
    links.push({
      label: t('sidebar.links.taxFriends'),
      path: '/referral',
      icon: 'referral',
      shellIcon: 'referral',
    });
  plannerAvailable &&
    links.push({
      label: t('taxPlanner.nav.label'),
      path: '/tax-planner',
      icon: 'paper',
      shellIcon: 'planner',
    });

  if (ENABLE_SYNC_UNIT_CONTROLLER) {
    // Under the sync flag the navigation follows the mock: a row on top below md, a column in the 230 px sidebar from md.
    return (
      <nav className="flex md:flex-col gap-1 px-3 min-w-0 flex-1 md:flex-none overflow-x-auto">
        {links.map((link) => (
          <NavItem key={link.label} to={link.path} icon={link.shellIcon || link.icon}>
            {link.label}
            <NavCounter count={link.count} label={t('syncUnits.nav.counter', { count: link.count })} />
          </NavItem>
        ))}
      </nav>
    );
  }

  const renderedLinks = links.map((link) => {
    return (
      <Link
        key={link.label}
        to={link.path}
        className="
          mx-[20px] lg:mx-0 flex justify-center lg:justify-normal lg:flex-none lg:h-auto h-[60px] lg:pb-3 text-sm lg:w-auto lg:pl-7 lg:py-3 text-gray-350 group lg:pr-6 whitespace-nowrap
          xs:mx-0 xs:items-center xs:w-[40px] xs:pb-1
          sm:mx-0 sm:items-center sm:w-[40px]
        "
        activeClassName={ACTIVE_CLASS}
        icon={link.icon}
        iconSize={20}
        iconClass="lg:mr-2 mr-0 w-[20px] h-[20px]">
        <span className="hidden lg:inline-block">{link.label}</span>
        <NavCounter count={link.count} label={t('syncUnits.nav.counter', { count: link.count })} />
        <ActiveLink />
      </Link>
    );
  });

  return (
    <div
      className="flex lg:flex-col justify-center 
    bg-gray-25 lg:bg-transparent lg:justify-normal
    lg:rounded-t-0 rounded-t-3xl items-center lg:items-stretch md:gap-0
    xs:gap-5 xs:justify-evenly xs:items-center xs:border xs:border-gray-200 xs:overflow-hidden
    sm:gap-5 sm:justify-evenly sm:items-center
    ">
      {renderedLinks}
    </div>
  );
}

export default Sidebar;
